import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      const yardageSelect = form.querySelectorAll('select[class*="appearance-none"]')[1] as HTMLSelectElement;
      const addressInput = form.querySelector('input[placeholder="123 Main St, Tacoma, WA"]') as HTMLInputElement;
      await sendFormEmail({
        subject: '253Gravel — New Contact / Quote Request',
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        Email: formData.email,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageSelect?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-stone-100 dark:bg-[#3a3a3a] border border-stone-300 dark:border-[#555555] rounded-none px-4 py-3 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#fdfbf7] dark:bg-[#373737] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-1081">
      <div className="max-w-6xl w-full mx-auto" data-id="element-1082">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-1083">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-1084">
            Contact & <span className="text-brand-orange" data-id="element-1085">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-1086">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1087">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-white dark:bg-[#474747] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#555555] shadow-lg" data-id="element-1088">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-1089">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-1090">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1091" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-1092">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-1093">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-1094">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1095">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1096">
                  <div data-id="element-1097">
                    <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1098">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-1099" />
                  </div>
                  <div data-id="element-1100">
                    <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1101">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-1102" />
                  </div>
                </div>

                <div data-id="element-1103">
                  <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1104">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-1105" />
                </div>

                <div data-id="element-1106">
                  <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1107">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-1108" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1109">
                  <div data-id="element-1110">
                    <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1111">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1112">
                      <option data-id="element-1113">Crushed Rock</option>
                      <option data-id="element-1114">Pea Gravel</option>
                      <option data-id="element-1115">Drain Rock</option>
                      <option data-id="element-1116">Road Base</option>
                      <option data-id="element-1117">Sand/Topsoil</option>
                      <option data-id="element-1118">Bark & Mulch</option>
                      <option data-id="element-1119">Sand</option>
                      <option data-id="element-1120">River Rock</option>
                      <option data-id="element-1121">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-1122">
                    <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1123">
                      Estimated Yardage
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1124">
                      <option data-id="element-1125">5 Yards (Minimum)</option>
                      <option data-id="element-1126">10 Yards</option>
                      <option data-id="element-1127">15 Yards</option>
                      <option data-id="element-1128">20+ Yards</option>
                      <option data-id="element-1129">Need help calculating</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-1130">
                  <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1131">
                    Delivery Address *
                  </label>
                  <input type="text" required className={inputClass} placeholder="123 Main St, Tacoma, WA" data-id="element-1132" />
                </div>

                <div data-id="element-1133">
                  <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1134">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-1135"></textarea>
                </div>

                <div className="pt-2" data-id="element-1136">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-1137">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1138" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1139" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-1140">
            {/* Contact Info Card */}
            <div className="bg-white dark:bg-[#474747] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#555555] shadow-lg" data-id="element-1141">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-1142">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-1143">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-1144">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1145">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-1146" />
                  </div>
                  <div data-id="element-1147">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1148">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1149">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@253gravel.com" className="flex items-center group" data-id="element-1150">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1151">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-1152" />
                  </div>
                  <div data-id="element-1153">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1154">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1155">
                      info@253gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-1156">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-1157">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-1158" />
                  </div>
                  <div data-id="element-1159">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1160">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1161">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white dark:bg-[#474747] rounded-2xl p-6 border border-stone-200 dark:border-[#555555] shadow-lg" data-id="element-1162">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-1163">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-1164" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-1165">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#555555]" data-id="element-1166">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1167">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-1168">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#555555]" data-id="element-1169">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1170">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-1171">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1172">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1173">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-1174">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-1175">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-1176">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-1177">
                Proudly serving Tacoma, Puyallup, Bonney Lake, Federal Way,
                Auburn, Kent, Lakewood, and surrounding South King County
                communities with reliable, same-day and next-day delivery. Not
                sure if you're in our delivery zone? Give us a call — we're
                happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}